/** Modelos partilhados: o estado partilhado pelos painéis, e as acções.
 *
 *  Um store pequeno fora do zustand da aplicação, de propósito: só existe
 *  com o módulo "modelos" activo, e quem o usa (barra lateral, sala de
 *  revisão, reposição de sessão, menu Sessão) lê-o com
 *  `useModelosPartilhados()`. A lista refresca-se de minuto a minuto
 *  enquanto houver quem a esteja a ver e o separador estiver visível: é
 *  assim que aparece o aviso de revisão nova.
 */
import { useContext, useEffect, useRef, useSyncExternalStore } from 'react'
import { AuthContext } from '@core/auth/useAuth'
import { ApiError } from '@core/api/client'
import { useAppStore } from '@core/store'
import { ifcPersistence } from '@core/session/ifcPersistence'
import { rawIfc } from '@viewer/lib/rawIfc'
import { viewerApi } from '@viewer/lib/viewerApi'
import type { LoadedModel } from '@viewer/store/viewerSlice'
import {
  descarregarModelo,
  enviarModelo,
  modelosApi,
} from './modelosApi'
import {
  chaveDoNome,
  encontrarPorSha,
  formatarBytes,
  revisoesNovasParaAvisar,
  textoDeConfirmacaoDaRevisao,
  type EstadoModelosServidor,
  type ModeloPartilhado,
} from './modelosRegras'
import { textoDeApagarModelo } from './reciclagemRegras'
import { abrirJanelaPartilhar } from '@core/partilhas/partilhasEstado'

export const MODULO_MODELOS = 'modelos'
const INTERVALO_MS = 60_000
const PERGUNTAR_KEY = 'vn2r.modelos.perguntarAoAbrir'

export interface Operacao {
  tipo: 'envio' | 'descarga'
  nome: string
  feito: number
  total: number
}

export interface EstadoModelos {
  estado: EstadoModelosServidor | null
  lista: ModeloPartilhado[] | null
  erro: string | null
  /** Operações em curso, por SHA-256. */
  operacoes: Record<string, Operacao>
}

let atual: EstadoModelos = { estado: null, lista: null, erro: null, operacoes: {} }
const ouvintes = new Set<() => void>()

function mudar(parcial: Partial<EstadoModelos>): void {
  atual = { ...atual, ...parcial }
  for (const f of ouvintes) f()
}

function subscrever(f: () => void): () => void {
  ouvintes.add(f)
  return () => ouvintes.delete(f)
}

function mensagem(err: unknown): string {
  return err instanceof Error ? err.message : 'Não foi possível falar com o servidor.'
}

let pedidoEmCurso: Promise<void> | null = null

/** Lê o estado e a lista. Pedidos sobrepostos juntam-se num só. */
export function recarregarModelos(): Promise<void> {
  if (pedidoEmCurso) return pedidoEmCurso
  pedidoEmCurso = (async () => {
    try {
      const [estado, lista] = await Promise.all([modelosApi.estado(), modelosApi.listar()])
      avisarRevisoesNovas(atual.lista, lista)
      mudar({ estado, lista, erro: null })
    } catch (err) {
      mudar({ erro: mensagem(err) })
    } finally {
      pedidoEmCurso = null
    }
  })()
  return pedidoEmCurso
}

function avisarRevisoesNovas(
  anterior: ModeloPartilhado[] | null,
  lista: ModeloPartilhado[],
): void {
  const abertos = useAppStore.getState().models.map((m) => m.sha256)
  for (const nova of revisoesNovasParaAvisar(anterior, lista, abertos)) {
    useAppStore.getState().pushToast(
      `Há uma revisão nova de "${nova.nome}" no workspace (revisão ${nova.revisao}`
        + `${nova.carregado_por_nome ? `, de ${nova.carregado_por_nome}` : ''}).`,
      {
        type: 'info',
        timeout: 0,
        acao: { rotulo: 'Abrir', executar: () => void abrirModeloPartilhado(nova) },
      },
    )
  }
}

// -- refrescamento enquanto há quem veja ---------------------------------

let consumidores = 0
let temporizador: ReturnType<typeof setInterval> | null = null

function visivel(): boolean {
  return typeof document === 'undefined' || document.visibilityState !== 'hidden'
}

function aoVoltar(): void {
  if (visivel()) void recarregarModelos()
}

function entrar(): void {
  consumidores++
  if (consumidores > 1) return
  void recarregarModelos()
  temporizador = setInterval(() => {
    if (visivel()) void recarregarModelos()
  }, INTERVALO_MS)
  document.addEventListener('visibilitychange', aoVoltar)
}

function sair(): void {
  consumidores = Math.max(0, consumidores - 1)
  if (consumidores > 0) return
  if (temporizador) clearInterval(temporizador)
  temporizador = null
  document.removeEventListener('visibilitychange', aoVoltar)
}

/** O módulo está ligado para quem está a usar a aplicação? Só em modo
 *  cloud, com o módulo activo no workspace e concedido ao membro. */
export function useModulosModelosLigado(): boolean {
  const auth = useContext(AuthContext)
  return !!auth && auth.mode === 'cloud' && !!auth.workspace
    && auth.modules.includes(MODULO_MODELOS)
}

/** O estado dos modelos partilhados, ou `null` com o módulo desligado.
 *  Montar este hook mantém a lista actualizada. */
export function useModelosPartilhados(): EstadoModelos | null {
  const ligado = useModulosModelosLigado()
  const estado = useSyncExternalStore(subscrever, () => atual, () => atual)
  useEffect(() => {
    if (!ligado) return
    entrar()
    return sair
  }, [ligado])
  return ligado ? estado : null
}

/** Para código fora do React (reposição de sessão). */
export function modelosPartilhadosAgora(): EstadoModelos {
  return atual
}

// -- acções ----------------------------------------------------------------

function marcarOperacao(sha: string, op: Operacao | null): void {
  const operacoes = { ...atual.operacoes }
  if (op) operacoes[sha] = op
  else delete operacoes[sha]
  mudar({ operacoes })
}

async function bytesDoModelo(m: LoadedModel): Promise<Blob | null> {
  const emMemoria = rawIfc.getCachedBytes(m.id)
  if (emMemoria) return new Blob([emMemoria as Uint8Array<ArrayBuffer>])
  const rec = await ifcPersistence.load(m.sha256)
  return rec?.blob ?? null
}

/** "Partilhar com...": a decisão de tirar o ficheiro da máquina, e com
 *  quem. Para um nome novo abre a janela de partilha (o workspace
 *  inteiro e/ou pessoas), que comprime, envia e confirma quando se
 *  carrega em "Partilhar". Uma revisão nova de um nome que já está
 *  partilhado fica com a lista de acessos desse nome: pede-se só
 *  confirmação, dizendo quem a vai ver.
 *  Devolve true se ficou partilhado ou se a janela abriu. */
export async function partilharModelo(m: LoadedModel): Promise<boolean> {
  const toast = useAppStore.getState().pushToast
  if (m.sourceFormat === 'frag' || m.rawIfcAvailable === false) {
    toast('Só se partilham ficheiros IFC. Este modelo foi aberto em Fragments.', {
      type: 'error',
    })
    return false
  }
  if (encontrarPorSha(atual.lista, m.sha256)) {
    toast(`"${m.fileName}" já está partilhado no workspace.`, { type: 'info' })
    return true
  }
  const ficheiro = await bytesDoModelo(m)
  if (!ficheiro) {
    toast(
      'Os bytes deste modelo já não estão em memória nem na cache do browser. '
        + 'Abra-o outra vez do disco e partilhe de novo.',
      { type: 'error', timeout: 8000 },
    )
    return false
  }
  const doMesmoNome = (atual.lista ?? []).find(
    (x) => x.ultima && chaveDoNome(x.nome) === chaveDoNome(m.fileName),
  )
  if (doMesmoNome) {
    if (!window.confirm(textoDeConfirmacaoDaRevisao(m.fileName, ficheiro.size, doMesmoNome.visivel_para))) {
      return false
    }
    return (await enviar(m, ficheiro, true)) !== null
  }
  abrirJanelaPartilhar({
    tipo: 'modelo',
    nome: `${m.fileName} (${formatarBytes(ficheiro.size)})`,
    partilhaId: null,
    criar: async (workspaceInteiro) => {
      const modelo = await enviar(m, ficheiro, workspaceInteiro, true)
      if (!modelo?.partilha_id) {
        throw new Error('O modelo foi enviado, mas o servidor não devolveu a partilha. Atualize a lista.')
      }
      return modelo.partilha_id
    },
    aoMudar: () => void recarregarModelos(),
  })
  return true
}

/** Para "Partilhar sessão": garante que o modelo aberto está no servidor
 *  e devolve-o (com a partilha). O que já lá está não volta a subir;
 *  o que falta sobe pelo caminho de sempre (comprimido, em blocos, com
 *  os limites e a quota do workspace), só para quem o envia: a sessão
 *  dá-lhe depois a lista dela. Um erro sobe com uma mensagem que diz o
 *  modelo e o motivo (sem espaço, grande de mais, nome já partilhado
 *  por outra pessoa...). */
export async function garantirModeloNoServidor(
  m: LoadedModel,
  aoProgredir?: (feito: number, total: number) => void,
): Promise<ModeloPartilhado> {
  const existente = encontrarPorSha(atual.lista, m.sha256)
  if (existente) return existente
  if (m.sourceFormat === 'frag' || m.rawIfcAvailable === false) {
    throw new Error(`"${m.fileName}" foi aberto em Fragments: só se partilham ficheiros IFC.`)
  }
  const ficheiro = await bytesDoModelo(m)
  if (!ficheiro) {
    throw new Error(
      `Os bytes de "${m.fileName}" já não estão em memória nem na cache do browser. `
        + 'Abra-o outra vez do disco e partilhe a sessão de novo.',
    )
  }
  marcarOperacao(m.sha256, { tipo: 'envio', nome: m.fileName, feito: 0, total: 1 })
  try {
    return await enviarModelo({
      nome: m.fileName,
      ficheiro,
      sha256: m.sha256,
      schemaIfc: m.schemaVersion,
      workspaceInteiro: false,
      aoProgredir: (feito, total) => {
        marcarOperacao(m.sha256, { tipo: 'envio', nome: m.fileName, feito, total })
        aoProgredir?.(feito, total)
      },
    })
  } catch (err) {
    // Alguém o enviou entretanto (ou a lista estava atrasada).
    if (err instanceof ApiError && err.status === 409) {
      await recarregarModelos()
      const ja = encontrarPorSha(atual.lista, m.sha256)
      if (ja) return ja
    }
    throw new Error(`Não foi possível enviar "${m.fileName}" para o servidor: ${mensagem(err)}`, {
      cause: err,
    })
  } finally {
    marcarOperacao(m.sha256, null)
    void recarregarModelos()
  }
}

/** Comprime, envia e confirma. Com `lancar`, um erro sobe (para a
 *  janela de partilha o mostrar); sem ele, vira um aviso. */
async function enviar(
  m: LoadedModel,
  ficheiro: Blob,
  workspaceInteiro: boolean,
  lancar = false,
): Promise<ModeloPartilhado | null> {
  const toast = useAppStore.getState().pushToast
  marcarOperacao(m.sha256, { tipo: 'envio', nome: m.fileName, feito: 0, total: 1 })
  try {
    const modelo = await enviarModelo({
      nome: m.fileName,
      ficheiro,
      sha256: m.sha256,
      schemaIfc: m.schemaVersion,
      workspaceInteiro,
      aoProgredir: (feito, total) =>
        marcarOperacao(m.sha256, { tipo: 'envio', nome: m.fileName, feito, total }),
    })
    toast(`"${modelo.nome}" partilhado (revisão ${modelo.revisao}).`, { type: 'success' })
    return modelo
  } catch (err) {
    if (err instanceof ApiError && err.status === 409 && /já está no workspace/.test(err.message)) {
      toast(err.message, { type: 'info', timeout: 8000 })
      if (lancar) throw err
      return null
    }
    if (lancar) throw err
    toast(`Não foi possível partilhar "${m.fileName}": ${mensagem(err)}`, {
      type: 'error',
      timeout: 0,
    })
    return null
  } finally {
    marcarOperacao(m.sha256, null)
    void recarregarModelos()
  }
}

export type ResultadoDeAbrir = 'aberto' | 'ja-aberto' | 'falhou'

/** Abre um modelo partilhado como se fosse um IFC local. Primeiro a
 *  cache do browser (pelo SHA-256): se já foi aberto aqui, não se
 *  descarrega outra vez. O loader guarda-o na cache ao abrir. */
export async function abrirModeloPartilhado(m: ModeloPartilhado): Promise<ResultadoDeAbrir> {
  const store = useAppStore.getState()
  const aberto = store.models.find((x) => x.sha256.toLowerCase() === m.sha256.toLowerCase())
  if (aberto) {
    store.setActiveModel(aberto.id)
    return 'ja-aberto'
  }
  if (!viewerApi.hasIfcLoader()) {
    store.pushToast('O viewer ainda não está pronto. Tente de novo dentro de instantes.', {
      type: 'error',
    })
    return 'falhou'
  }
  try {
    let ficheiro: File
    const rec = await ifcPersistence.load(m.sha256)
    if (rec) {
      ficheiro = ifcPersistence.toFile(rec)
    } else {
      marcarOperacao(m.sha256, { tipo: 'descarga', nome: m.nome, feito: 0, total: 1 })
      const { bytes } = await descarregarModelo(m.id, (feito, total) =>
        marcarOperacao(m.sha256, { tipo: 'descarga', nome: m.nome, feito, total }),
      )
      ficheiro = new File([bytes], m.nome, { type: 'application/octet-stream' })
    }
    const r = await viewerApi.loadIfcFile(ficheiro, { replace: false })
    if (!r.ok) {
      store.pushToast(`Não foi possível abrir "${m.nome}": ${r.erro}`, { type: 'error' })
      return 'falhou'
    }
    return 'aberto'
  } catch (err) {
    store.pushToast(`Não foi possível abrir "${m.nome}": ${mensagem(err)}`, {
      type: 'error',
      timeout: 0,
    })
    return 'falhou'
  } finally {
    marcarOperacao(m.sha256, null)
  }
}

export async function apagarModeloPartilhado(m: ModeloPartilhado): Promise<void> {
  if (!window.confirm(textoDeApagarModelo(m.nome, m.revisao))) return
  try {
    await modelosApi.apagar(m.id)
    useAppStore.getState().pushToast(
      `Revisão ${m.revisao} de "${m.nome}" enviada para a Reciclagem.`,
      { type: 'success' },
    )
  } catch (err) {
    useAppStore.getState().pushToast(`Não foi possível apagar: ${mensagem(err)}`, {
      type: 'error',
    })
  } finally {
    void recarregarModelos()
  }
}

export async function definirQuotaModelos(quotaMb: number | null): Promise<void> {
  try {
    const estado = await modelosApi.definirQuota(quotaMb)
    mudar({ estado })
  } catch (err) {
    useAppStore.getState().pushToast(`Não foi possível alterar o limite: ${mensagem(err)}`, {
      type: 'error',
    })
  }
}

// -- pergunta ao abrir ----------------------------------------------------

export function perguntarAoAbrir(): boolean {
  try {
    return localStorage.getItem(PERGUNTAR_KEY) !== '0'
  } catch {
    return true
  }
}

export function definirPerguntarAoAbrir(sim: boolean): void {
  try {
    localStorage.setItem(PERGUNTAR_KEY, sim ? '1' : '0')
  } catch {
    /* sem onde guardar: vale para esta sessão */
  }
  mudar({})
}

/** Ao abrir um IFC novo, oferece "Partilhar no workspace" num aviso
 *  que não bloqueia. Só para modelos que entram depois de o painel
 *  estar montado e com a lista já lida: os que já estavam abertos, e os
 *  que vieram da própria lista, não perguntam. */
export function usePerguntarPartilhaAoAbrir(
  estado: EstadoModelos | null,
  models: readonly LoadedModel[],
): void {
  const lista = estado?.lista ?? null
  const ativo = !!estado?.estado?.ativo
  const vistosRef = useRef<Set<string> | null>(null)
  useEffect(() => {
    if (vistosRef.current === null) {
      // Os que já estavam abertos quando o painel montou não perguntam.
      vistosRef.current = new Set(models.map((m) => m.id))
      return
    }
    const vistos = vistosRef.current
    for (const m of models) {
      if (vistos.has(m.id)) continue
      vistos.add(m.id)
      if (!ativo || !lista || !perguntarAoAbrir()) continue
      if (m.sourceFormat === 'frag' || m.rawIfcAvailable === false) continue
      if (encontrarPorSha(lista, m.sha256)) continue
      useAppStore.getState().pushToast(
        `Abriu "${m.fileName}". Pode partilhá-lo com o workspace ou com pessoas `
          + 'concretas, para abrirem o mesmo ficheiro; se não o fizer, fica só na sua máquina.',
        {
          type: 'info',
          timeout: 15_000,
          acao: { rotulo: 'Partilhar com…', executar: () => void partilharModelo(m) },
        },
      )
    }
  }, [models, lista, ativo])
}
